import React from 'react';
import { computed, observable, action, reaction, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import {  TextField, mergeStyleSets } from '@fluentui/react';
import { runShortAction  } from 'src/services/utils';
import office from 'src/services/office';

import Scheduler from 'src/components/Scheduler';

import GroupPane from 'src/components/base/GroupPane';
import ConfirmDialog from 'src/components/base/ConfirmDialog';
import DefaultButtonEx from '@v2/components/base/DefaultButtonEx'

import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';

import { Type, getRoute, generateUuid } from 'shared/utils/flow';
import { CURRENT_VERSION } from 'shared/migration/flow';
import CellAddressPicker from '@v2/components/base/CellAddressPicker';
import { toCamelCase } from '../../../../../shared/utils/utils';
import * as Flow from 'shared/utils/flow';


const classNames = mergeStyleSets({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  content: {
    margin: '0 16px',
    selectors: {
      '& > div': {
        marginTop: '16px',
      },
    }
  },

  codemirror: {
    border: '1px solid #A6A6A6',
    opacity: 0.8,
    margin: 16,
  },
  block: {
    margin: 16,
  },
  buttonBlock: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'stretch',
    margin: '16px 0px 0px -16px',
    selectors: {
      '& > *': {
        flexGrow: 1,
        marginLeft: 16,
      },
    },
  },
  options: {
    margin: '16px 0px 0px 0px',
  },
  optionsContent: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        // display: 'flex',
        // flexDirection: 'row',
        // justifyContent: 'space-between',
        // alignItems: 'center',
        marginTop: '16px',
      },
    },
  },
  optionsContentV: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 14,
      },
    },
  },

  tsPicker: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    selectors: {
      '& > :nth-child(1)': {
        flexShrink: 0,
      },
      '& > :nth-child(2)': {
        marginLeft: '8px',
        marginRight: '8px',
        textAlign: 'center',
      },
    },
  },
});


@inject('dataStore')
@inject('officeStore')
@observer
export default class FlowEditor extends React.Component {
  @observable isDirty = false;
  @observable showDeleteFlowConfirmation = false;
  
  @observable flow = null;
  @observable name = '';
  @observable lastUpdateWorksheetName = '';
  
  @observable options = {
    lastUpdateTimestampCell: null,
    runButtonCell: null,
    recalculatePivotTables: false,
  };

  @observable optionsValidation = {
    name: false,
    lastUpdateTimestampCell: true,
    runButtonCell: true,
  };
  
  @computed get generatedName() { return 'New flow'; }

  @computed get areAllOptionsValid() {
    return Object.values(this.optionsValidation).every(x => !!x)
  }

  @computed get isBuiltinFlow() {
    return [Flow.Type.Pull, Flow.Type.Push].includes(this.flow?.type)
  }

  constructor(props) {
    super(props);
    makeObservable(this);

    reaction(
      () => ({ isDirty: this.isDirty, isValid: this.areAllOptionsValid }),
      () => {
        this.props.pageControl.setDirtyActions(this.isDirty && this.flow
          ? {
              text: 'You have unsaved changes in the flow',
              onSave: () => this.handleSaveFlow(),
              disableSave: !this.areAllOptionsValid
            }
          : null);
    });
  }

  // --- Render ---

  render() {
    return (
      <div className={classNames.root}>
        <div className={classNames.content}>

          <TextField
            label="Flow name"
            required
            disabled={this.flow && this.flow.type !== Type.Custom}
            placeholder={'Name this flow'}
            value={this.name}
            styles={{ wrapper: { width: '100%' } }}
            onChange={(_, value) => this.handleNameChange(value) }
          />

        </div>

        {this.flow &&
          <GroupPane className={classNames.options} collapsed={false} title='Automations'>
            <div className={classNames.optionsContentV}>
              <Scheduler flowId={this.flow.id} />
            </div>
          </GroupPane>
        }

        <GroupPane title='Run Button' className={classNames.options} >
          <div className={classNames.optionsContent}>
            {"Select a range to create a clickable button in it that will run all steps in this flow"}
            <CellAddressPicker
              label={"Range:"}
              value={this.options.runButtonCell}
              onChange={(value, isValid) => this.handleRunButtonCellChange(value, isValid)}
              allowEmptyCell={true}
              disableSingleCell
              localAddressSheetIdGetter={() => this.props.officeStore.activeWorksheetId}
            />
          </div>
        </GroupPane>
        <GroupPane className={classNames.options} title='Options'>
          <div className={classNames.optionsContent}>
            <CellAddressPicker
              label={"Last run time:"}
              value={this.options.lastUpdateTimestampCell}
              onChange={(value, isValid) => this.handleLastUpdateTimestampCellChange(value, isValid)}
              allowEmptyCell={true}
              localAddressSheetIdGetter={() => this.props.officeStore.activeWorksheetId}
            />
            <div>
              <CheckboxWithTooltip
                label={'Recalculate pivot tables at the end of Flow execution'}
                checked={this.options.recalculatePivotTables}
                onChange={(_, checked) => this.handleOptionsChange('recalculatePivotTables', checked)}
              />
            </div>
          </div>
        </GroupPane>
        

        <div className={classNames.block}>
          <div className={classNames.buttonBlock}>
            <DefaultButtonEx text='Delete'
              tooltip={this.isBuiltinFlow ? 'You can’t delete built-in flows' : 'Deletes the current flow'}
              onClick={() => this.showDeleteFlowConfirmation = true}
              disabled={!this.flow || this.isBuiltinFlow}
            />
            <DefaultButtonEx text='Save'
              tooltip={'Updates current flow'}
              onClick={this.handleSaveFlow} primary={true}
              disabled={!this.areAllOptionsValid}
            />
          </div>

        </div>

        <ConfirmDialog
          title={'Delete the Flow?'}
          text={'Are you sure you want to permanently remove this flow?'}
          hidden={!this.showDeleteFlowConfirmation}
          onConfirm={this.handleDeleteFlow}
          onDismiss={() => this.showDeleteFlowConfirmation = false}
        />

      </div>
    )
  }


  async componentDidMount() {
    await runShortAction(async () => {
      await this.props.officeStore.contextStore.updateNamedRanges();
      const { id } = this.props.computedMatch.params;
      const { dataStore } = this.props;
      if (id) {
        const flow = dataStore.getFlow(id);
        flow && this.loadFlow(flow);
      }
    });
  }

  loadFlow(flow) {
    const { lastUpdateTimestampCell, recalculatePivotTables, runButtonCell } = flow.options;
    
    this.name = flow.name;
    this.optionsValidation.name = !!this.name;
    this.options.lastUpdateTimestampCell = lastUpdateTimestampCell;
    this.options.runButtonCell = runButtonCell;
    this.options.recalculatePivotTables = recalculatePivotTables;
    this.flow = flow;
  }

  async makeFlow()  {
    const { lastUpdateTimestampCell, recalculatePivotTables } = this.options;
    let { runButtonCell } = this.options;
    
    const flowName = this.name || this.generatedName;

    if (runButtonCell?.address && runButtonCell?.worksheetId) {
      let rangeName = `RunButton_${toCamelCase(flowName)}`;
      runButtonCell.customRange = rangeName;
    }

    const flow = {
      name: flowName,
      id: this.flow?.id ?? generateUuid(),
      type: this.flow?.type ?? Type.Custom,
      version: CURRENT_VERSION,
      options: { 
        flowsteps: this.flow?.options.flowsteps ?? [],
        lastUpdateTimestampCell, 
  
        recalculatePivotTables,
        
        runButtonCell,
      },
    };

    return flow;
  }

  // --- Options ---

  setDirty = (isDirty = true) => {
    this.isDirty = isDirty;
  }

  @action handleNameChange = (name) => {
    this.name = name;
    this.optionsValidation.name = !!name;
    this.setDirty();
  }

  @action handleOptionsChange = (index, value) => {
    this.options[index] = value;
    this.setDirty();
  }

  @action handleLastUpdateTimestampCellChange = (value, isValid) => {
    this.options.lastUpdateTimestampCell = value;
    this.optionsValidation.lastUpdateTimestampCell = isValid;
    this.setDirty();
  }

  @action handleRunButtonCellChange = (value, isValid) => {
    this.options.runButtonCell = value;
    this.optionsValidation.runButtonCell = isValid;
    this.setDirty();
  }

  updateRunButton = async (oldButton, { newButton, text }) => {
    const isNewRunButtonEqualsOld = JSON.stringify(newButton) === JSON.stringify(oldButton);
    const isNeedToAddRunButton = newButton && (!oldButton || !isNewRunButtonEqualsOld);
    const isNeedToDeleteRunButton = oldButton && (!newButton || !isNewRunButtonEqualsOld);

    if (isNeedToDeleteRunButton) {
      await this.props.officeStore.contextStore.runTaskAndRefreshRanges(
        async () => await office.deleteRunButton(oldButton)
      );
    }
    if (isNeedToAddRunButton) {
      const { customRange: rangeName, worksheetId, address } = newButton;
      await this.props.officeStore.contextStore.runTaskAndRefreshRanges(
        async () => await office.createNamedRange(rangeName, worksheetId, address)
      );

      // we use only range
      newButton.worksheetId = undefined;
      newButton.address = undefined;

      await office.createRunButton(newButton, text);
    }
  }

  // --- Buttons ---

  handleSaveFlow = async () => {
    await runShortAction(async () => {
      const flow = await this.makeFlow();
      await this.updateRunButton(this.flow?.options.runButtonCell, { newButton: flow.options.runButtonCell, text: flow.name });
      await this.props.dataStore.createOrUpdateFlow(flow);
      
      this.flow = flow;
      this.setDirty(false);
      this.goBack();
    });
  };

  handleDeleteFlow = async () => {
    this.showDeleteConfirmation = false

    await runShortAction(async () => {
      await this.props.dataStore.deleteFlow(this.flow);
      this.setDirty(false);
      this.goBack();
    });
  }

  goBack() {
    this.flow
      ? this.props.history.replace(getRoute({ selectedId: this.flow.id }))
      : this.props.goBack();
  }
}
